import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRefState } from 'src/shared/hooks/useRefState';

const FOOTER_HEIGHT = 64;

interface ScrollNavigationItem<T = string> {
  id: T;
  element: HTMLDivElement | null;
}

interface UseScrollNavigationOptions<T = string> {
  items: { id: T }[];
  navHeightCalculator?: (navElement: HTMLDivElement | null) => number;
  scrollContainer?: HTMLElement | null;
  /** Jumped to without animation once the nav and this item have mounted, as for a deep link. */
  initialItemId?: T;
}

/**
 * Generic hook for scroll-based navigation with active section detection
 * Can be used for any navigation that needs to:
 * 1. Track which section is currently active based on scroll position
 * 2. Smoothly scroll to sections when nav items are clicked
 * 3. Calculate navigation height dynamically
 */
export const useScrollNavigation = <T extends string = string>({
  items,
  navHeightCalculator,
  scrollContainer,
  initialItemId,
}: UseScrollNavigationOptions<T>) => {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const [navElement, setNavElement] = useRefState<HTMLDivElement | null>();
  const [activeItemId, setActiveItemId] = useState<T | undefined>(items[0]?.id);

  const setItemRef = useCallback((itemId: T, el: HTMLDivElement | null) => {
    refs.current[itemId] = el;
  }, []);

  // Calculate nav height dynamically based on actual nav element
  const navHeight = useMemo(() => {
    if (navHeightCalculator) {
      return navHeightCalculator(navElement);
    }

    if (!navElement) return 100; // fallback value
    const navRect = navElement.getBoundingClientRect();
    return navRect.top + navRect.height;
  }, [navElement, navHeightCalculator]);

  const handleItemClick = useCallback(
    (itemId: T, behavior: ScrollBehavior = 'smooth') => {
      setActiveItemId(itemId);
      const element = refs.current[itemId];
      if (!element || !navElement) return;

      if (scrollContainer) {
        // Container scrolling
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const elementPosition = elementRect.top - containerRect.top + scrollContainer.scrollTop;
        const offsetPosition = elementPosition - navHeight + 1; // + 1 offset to improve active detection

        scrollContainer.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior,
        });
      } else {
        // Window scrolling (default)
        const elementPosition = element.getBoundingClientRect().top;
        // + 1 offset to improve active detection: the browser rounds the scroll position down
        const offsetPosition = elementPosition + window.scrollY - navHeight + 1;

        // eslint-disable-next-line ban/ban -- Need to scroll to specific position, not top
        window.scrollTo({
          top: offsetPosition,
          behavior,
        });
      }
    },
    [navHeight, navElement, scrollContainer]
  );

  const hasJumpedToInitialItemRef = useRef(false);
  useEffect(() => {
    if (isNil(initialItemId) || hasJumpedToInitialItemRef.current || !navElement || !refs.current[initialItemId]) {
      return;
    }
    hasJumpedToInitialItemRef.current = true;
    handleItemClick(initialItemId, 'auto');
  }, [initialItemId, navElement, handleItemClick, items]);

  // Scroll-based active section detection
  useEffect(() => {
    const calculateActiveItem = () => {
      if (!navElement) return;

      // Use container dimensions if provided, otherwise use window
      const container = scrollContainer;
      const containerHeight = container ? container.getBoundingClientRect().height : window.innerHeight;
      const viewportBottom = containerHeight - (container ? 0 : FOOTER_HEIGHT);
      const visibleHeight = containerHeight - navHeight - (container ? 0 : FOOTER_HEIGHT);

      const itemElements = items
        .map((item) => ({
          id: item.id,
          element: refs.current[item.id],
        }))
        .filter((item): item is ScrollNavigationItem<T> => !!item.element);

      if (itemElements.length === 0) return;

      const calculateItemIsActive = (element: HTMLDivElement, isLastElement: boolean) => {
        const elementRect = element.getBoundingClientRect();
        const elementHeight = elementRect.height;
        const elementTallerThanViewport = elementHeight >= visibleHeight;

        if (container) {
          // Container scrolling: calculate relative to container and use scroll coordinates
          const containerRect = container.getBoundingClientRect();
          const { top: containerTop } = containerRect;
          const { scrollTop } = container;
          const elTopRel = elementRect.top - containerTop;
          const elBotRel = elementRect.bottom - containerTop;
          // Threshold in scroll coordinates (navHeight from container top)
          const thresholdInScroll = scrollTop + navHeight;
          // Convert element top to scroll coordinates for comparison
          const elTopInScroll = elTopRel + scrollTop;

          if (!elementTallerThanViewport && isLastElement) {
            return elBotRel <= viewportBottom;
          }

          return elTopInScroll <= thresholdInScroll;
        }

        // Window scrolling: use absolute positions
        const elTop = Math.floor(elementRect.top);
        const elBot = Math.floor(elementRect.bottom);

        if (!elementTallerThanViewport && isLastElement) {
          return elBot <= viewportBottom;
        }

        return elTop <= navHeight;
      };

      const activeState = itemElements.map(({ element }, idx) => {
        const isLastElement = idx === itemElements.length - 1;
        return element ? calculateItemIsActive(element, isLastElement) : false;
      });

      const lastActiveIndex = activeState.lastIndexOf(true);
      if (lastActiveIndex !== -1) {
        const activeItem = itemElements[lastActiveIndex];
        if (activeItem && activeItem.id !== activeItemId) {
          setActiveItemId(activeItem.id);
        }
      }
    };

    // Initial calculation
    calculateActiveItem();

    // Use container scroll events if provided, otherwise use document scroll
    const scrollTarget = scrollContainer ?? document;
    scrollTarget.addEventListener('scroll', calculateActiveItem);

    return () => {
      scrollTarget.removeEventListener('scroll', calculateActiveItem);
    };
  }, [items, activeItemId, navElement, navHeight, scrollContainer]);

  return {
    activeItemId,
    handleItemClick,
    setItemRef,
    setNavElement,
    navHeight,
  };
};
